import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { OrdenPorFacturar, Pagina, ResumenFacturacion } from '../api/tipos';
import { Cargando, Error, Paginacion, Ordenable, useOrden } from '../componentes/comunes';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';
import CrearPrefactura from './CrearPrefactura';
import Prefacturas from './Prefacturas';
import IntegrarUuids from './IntegrarUuids';
import ComplementoDePagos from './ComplementoDePagos';
import Cerradas from './Cerradas';
import ReporteFacturacion from './ReporteFacturacion';
import ReporteCobranza from './ReporteCobranza';

/**
 * Facturación.
 *
 * APRISA no timbra. Lo que se arma aquí es una **prefactura**: el
 * registro de lo que se va a cobrar y el archivo que Master EDI levanta
 * para generar el XML y el PDF con el sello del SAT. Mientras no llegue
 * el UUID de vuelta, no hay factura: hay prefactura.
 *
 * Por eso el vocabulario de la pantalla no es decorativo. Llamarle
 * factura a algo que todavía no está timbrado invita a mandársela al
 * cliente, y lo que el cliente necesita es el CFDI.
 *
 * La sección son cinco bandejas: lo que falta por facturar, lo que ya se
 * armó, la captura, la vuelta del timbrado y los reportes. Son botones y
 * no rutas porque no cambian de dirección: se vuelve de una a otra sin
 * perder el filtro de la anterior.
 */

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function Tarjeta({ titulo, valor, alerta }: { titulo: string; valor: string; alerta?: boolean }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}

/**
 * Las órdenes cerradas que todavía no se facturan.
 *
 * Toda orden cerrada cae aquí, incluidas las que cubre la iguala: lo que
 * la iguala cubre también se cobra, dentro de ella. Lo que separa una de
 * otra no es si se factura, sino si además lleva cargo aparte y si ese
 * cargo ya lo validó Operaciones.
 */
function Pendientes({
  resumen, recarga, puedeFacturar, alFacturar,
}: {
  resumen: ResumenFacturacion | null;
  recarga: number;
  puedeFacturar: boolean;
  alFacturar: (ordenId: string) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<OrdenPorFacturar> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const { orden, alClicar } = useOrden(() => setNumero(1));

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<OrdenPorFacturar>>('/facturacion/por-facturar', {
      busqueda: busqueda || undefined,
      // Sin nada elegido manda el orden de la bandeja, que aquí es lo más
      // viejo arriba: la antigüedad es el problema de esta lista.
      ordenarPor: orden?.por,
      sentido: orden?.sentido,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, numero, orden]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda, recarga]);

  return (
    <>
      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Folio de la orden, orden de servicio o sucursal…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay órdenes por facturar con ese filtro.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <Ordenable por="folio" orden={orden} alClicar={alClicar}>Folio OT</Ordenable>
                    <Ordenable por="cliente" orden={orden} alClicar={alClicar}>Cliente</Ordenable>
                    <Ordenable por="sucursal" orden={orden} alClicar={alClicar}>Sucursal</Ordenable>
                    <Ordenable por="fecha_cierre" orden={orden} alClicar={alClicar} primerClic="desc">
                      Cerrada
                    </Ordenable>
                    {/* Los días son la misma fecha leída al revés, y aun
                        así se deja ordenar por los dos: son dos preguntas
                        distintas —«qué se cerró cuándo» y «qué lleva más
                        esperando»— y se clica la que uno trae en la
                        cabeza. El primer clic trae lo más atrasado. */}
                    <Ordenable
                      por="dias" orden={orden} alClicar={alClicar}
                      primerClic="desc" className="numero"
                    >
                      Días
                    </Ordenable>
                    <Ordenable
                      por="importe" orden={orden} alClicar={alClicar}
                      primerClic="desc" className="numero"
                    >
                      Importe
                    </Ordenable>
                    {/* La situación no se ordena: no es una columna, se
                        deduce de si la orden ya tiene precio, y para eso
                        está el filtro de arriba. */}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((o) => (
                    <tr key={o.id}>
                      <td className="mono">
                        {folioOT(o.folio)}
                        {o.folioServicio && (
                          <div className="conteo">OS {o.folioServicio}</div>
                        )}
                      </td>
                      <td>
                        {o.cliente?.nombre ?? '—'}
                        {/* Sin RFC no hay timbrado. Se avisa desde la bandeja
                            para que se consiga antes y no al intentar emitir. */}
                        {o.cliente && !o.cliente.rfc && (
                          <div className="conteo" style={{ color: '#b02a37' }}>sin RFC</div>
                        )}
                      </td>
                      <td>
                        {typeof o.sucursal === 'string'
                          ? o.sucursal
                          : o.sucursal
                            ? `${o.sucursal.determinante} — ${o.sucursal.nombre}`
                            : '—'}
                      </td>
                      <td>{o.fechaCierre ? fechaCorta(o.fechaCierre) : '—'}</td>
                      <td className="numero">
                        <span className={(o.diasDesdeCierre ?? 0) > 30 ? 'etiqueta no' : undefined}>
                          {o.diasDesdeCierre ?? '—'}
                        </span>
                      </td>
                      <td className="numero">
                        {o.tieneCargoAparte
                          ? (o.precioVenta !== null ? moneda(o.precioVenta) : '—')
                          // Sin cargo aparte, el importe sale de la iguala de
                          // la sucursal, que todavía no está capturada.
                          : <span className="conteo">iguala</span>}
                      </td>
                      <td>
                        {/* Se ofrece también sobre las que esperan costo: la
                            pantalla de captura enseña qué falta y no deja
                            generar, que sirve más que esconder el botón y
                            dejar a alguien sin saber por qué. */}
                        {puedeFacturar && (
                          <button className="enlace" onClick={() => alFacturar(o.id)}>
                            Facturar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {resumen && resumen.porCliente.length > 0 && (
        <div className="panel">
          <h2>Por cliente</h2>
          <p className="sub">
            Una prefactura puede agrupar varias órdenes del mismo cliente
            que compartan orden de compra. Este reparto es el punto de
            partida de ese agrupamiento.
          </p>
          <table>
            <thead>
              <tr><th>Cliente</th><th className="numero">Órdenes</th></tr>
            </thead>
            <tbody>
              {resumen.porCliente.map((c) => (
                <tr key={c.clienteId}>
                  <td>{c.cliente}</td>
                  <td className="numero">{c.ordenes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Las bandejas de la sección. Son botones, no rutas: no cambian de URL. */
type Vista =
  | 'pendientes' | 'cerradas' | 'prefacturas' | 'crear' | 'uuids' | 'pagos' | 'reportes';

export default function Facturacion() {
  const { puede } = useSesion();
  const [vista, setVista] = useState<Vista>('pendientes');
  const [reporte, setReporte] = useState<'facturado' | 'cobranza'>('facturado');
  // La orden que se está facturando. Nula en una prefactura libre.
  const [facturando, setFacturando] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [resumen, setResumen] = useState<ResumenFacturacion | null>(null);

  useEffect(() => {
    api.get<ResumenFacturacion>('/facturacion/resumen')
      .then(setResumen)
      .catch(() => undefined);
  }, [recarga]);

  /** Se creó una prefactura: la orden sale de la bandeja y se ve la lista. */
  function creada() {
    setFacturando(null);
    setRecarga((n) => n + 1);
    setVista('prefacturas');
  }

  const puedeFacturar = puede('facturacion', 'crear');

  /**
   * Integrar los UUID escribe: marca las prefacturas como timbradas.
   *
   * La pestaña estaba abierta a cualquiera con `facturacion.ver`, y eso
   * dejaba entrar a quien viene sólo por los reportes —contabilidad, que
   * necesita lo facturado para cuadrar el mes—. Podía subir el archivo y
   * ver la vista previa, porque las dos cosas son de lectura, y tronaba
   * con un 403 al final: el paso que sí escribe exige `editar`.
   *
   * Es el mismo defecto que se corrigió en almacén: un botón que promete
   * algo que no se puede hacer. Duele más aquí porque todo lo anterior
   * parece funcionar.
   */
  const puedeIntegrar = puede('facturacion', 'editar');

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Facturación</h1>
          <p className="sub">
            Órdenes cerradas pendientes de facturar. De aquí sale la
            prefactura y su archivo para Master EDI, que es quien timbra.
          </p>
        </div>
      </div>

      {resumen && (
        <div className="rejilla-catalogos">
          <Tarjeta titulo="Por facturar" valor={String(resumen.total)} />
          <Tarjeta
            titulo="Cerradas hace más de 30 días"
            valor={String(resumen.masDe30Dias)}
            alerta={resumen.masDe30Dias > 0}
          />
          <Tarjeta titulo="Importe con precio fijado" valor={moneda(resumen.importeConPrecio)} />
        </div>
      )}

      {/* Las pestañas van en el orden del trabajo, no en el que se
          fueron construyendo: una orden cae en Cerradas, alguien confirma
          su estatus del lado del cliente, pasa a Pendientes por facturar,
          se le hace su prefactura, se integra su UUID, se cobra y se
          reporta. Quien aprende la pantalla leyendo la fila de arriba
          aprende el proceso. */}
      <nav className="submenu">
        <button
          className={vista === 'cerradas' ? 'activo' : ''}
          onClick={() => setVista('cerradas')}
        >
          Cerradas
        </button>
        <button
          className={vista === 'pendientes' ? 'activo' : ''}
          onClick={() => setVista('pendientes')}
        >
          Pendientes por facturar
        </button>
        <button
          className={vista === 'prefacturas' ? 'activo' : ''}
          onClick={() => setVista('prefacturas')}
        >
          Prefacturas
        </button>
        {puedeFacturar && (
          <button
            className={vista === 'crear' ? 'activo' : ''}
            onClick={() => { setFacturando(null); setVista('crear'); }}
          >
            Crear prefactura
          </button>
        )}
        {puedeIntegrar && (
          <button
            className={vista === 'uuids' ? 'activo' : ''}
            onClick={() => setVista('uuids')}
          >
            Integrar UUIDs
          </button>
        )}
        {/* Registrar un pago da facturas por cobradas, o sea mueve
            dinero del cliente: exige `crear`, igual que emitir. Quien
            viene sólo por los reportes no debe ver la pestaña, para no
            toparse con un 403 después de haber subido el archivo. */}
        {puedeFacturar && (
          <button
            className={vista === 'pagos' ? 'activo' : ''}
            onClick={() => setVista('pagos')}
          >
            Complemento de pagos
          </button>
        )}
        <button
          className={vista === 'reportes' ? 'activo' : ''}
          onClick={() => setVista('reportes')}
        >
          Reportes
        </button>
      </nav>

      {vista === 'crear' && (
        <CrearPrefactura
          ordenTrabajoId={facturando}
          alCrear={creada}
          alCancelar={() => { setFacturando(null); setVista('pendientes'); }}
        />
      )}

      {/* Al pasar una orden cambia lo que hay en «Pendientes por
          facturar» y en las tarjetas de arriba, así que se recargan: si
          no, quien pasa diez órdenes sigue viendo el conteo de antes y
          cree que no se aplicó. */}
      {vista === 'cerradas' && <Cerradas alPasar={() => setRecarga((n) => n + 1)} />}

      {vista === 'prefacturas' && <Prefacturas recarga={recarga} />}

      {/* El permiso se comprueba también aquí y no sólo en la pestaña:
          esconder el botón basta hoy, porque es la única entrada, pero
          la vista es un estado y el día que otro camino la ponga, este
          renglón es el que evita que se cuele. */}
      {vista === 'uuids' && puedeIntegrar && <IntegrarUuids />}

      {vista === 'pagos' && puedeFacturar && <ComplementoDePagos />}

      {/* Dos reportes del mismo dinero, visto desde los dos lados: lo que
          se emitió y lo que entró. Van juntos porque quien abre uno abre
          el otro —contabilidad cuadra el mes con los dos— y separados
          porque el corte no es el mismo: uno va por fecha de emisión y el
          otro por fecha de depósito. */}
      {vista === 'reportes' && (
        <>
          <nav className="submenu" style={{ marginTop: -8 }}>
            <button
              className={reporte === 'facturado' ? 'activo' : ''}
              onClick={() => setReporte('facturado')}
            >
              Facturado
            </button>
            <button
              className={reporte === 'cobranza' ? 'activo' : ''}
              onClick={() => setReporte('cobranza')}
            >
              Cobranza
            </button>
          </nav>
          {reporte === 'facturado' ? <ReporteFacturacion /> : <ReporteCobranza />}
        </>
      )}

      {vista === 'pendientes' && (
        <Pendientes
          resumen={resumen}
          recarga={recarga}
          puedeFacturar={puedeFacturar}
          alFacturar={(id) => { setFacturando(id); setVista('crear'); }}
        />
      )}
    </>
  );
}
